import { App } from "@/constants";
import { CURRENT_APP_QUERY_PARAMETER } from "@/constants/current_app";
import {
    customerInfoProviderToken,
    globalLoggerToken,
    gwStorefrontAppInfoToken,
    localStorageToken,
    modalManagerToken,
} from "@/tokens";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import MinilogLogger from "@lib/Logger";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { BaseModule } from "@modules/base_module/BaseModule";
import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { SDK_MODULES } from "@modules/sdk/constants";
import { subscribeToSdkMethod } from "@modules/sdk/helpers";
import { SdkModule } from "@modules/sdk/SdkModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import { assert } from "superstruct";
import { container, inject, instanceCachingFactory, singleton } from "tsyringe";

import DrawerManager from "@components/Drawer/DrawerManager";
import { ApplicationInterface } from "@interfaces/ApplicationInterface";

import {
    PRODUCT_HANDLE_STORAGE_KEY,
    REGISTRY_FORM_MODAL_ACTION_TYPES,
} from "./constants";
import {
    AddToRegistryModalWidgetDefinition,
    AddToRegistryProductPageWidgetDefinition,
    GiftCartDrawerWidgetDefinition,
    GiftRegistryPageWidgetDefinition,
    RegistryFormModalWidgetDefinition,
    SharedRegistryPageWidgetDefinition,
} from "./definitions";
import { GIFT_REGISTRY_SDK_METHODS } from "./gift_registry_sdk/constants";
import OpenGiftRegistryFormModalSdkMethodPayload from "./gift_registry_sdk/methods/OpenGiftRegistryFormModalSdkMethodPayload";
import {
    GiftRegistryApplicationConfigInterface,
    GiftRegistryApplicationConfigSchema,
    GiftRegistryApplicationConfigToken,
} from "./GiftRegistryApplicationConfig";
import {
    giftCartDrawerManagerToken,
    giftRegistryApplicationLoggerToken,
    giftRegistryAuthApiClientToken,
    registryFormModalToken,
} from "./tokens";
import AddToRegistryModalWidgetInjector from "./widgets/add_to_registry_modal_widget/AddToRegistryModalWidgetInjector";
import AddToRegistryProductPageWidgetInjector from "./widgets/add_to_registry_product_page_widget/AddToRegistryProductPageWidgetInjector";
import GiftCartDrawerWidgetInjector from "./widgets/gift_cart_drawer_widget/GiftCartDrawerWidgetInjector";
import RegistryFormModalWidgetInjector from "./widgets/registry_form_modal_widget/RegistryFormModalWidgetInjector";
import SharedRegistryPageWidgetInjector from "./widgets/shared_registry_page_widget/SharedRegistryPageWidgetInjector";

@singleton()
export class GiftRegistryApplication implements ApplicationInterface<object> {
    widgets = [];
    exports = {};

    constructor(
        @inject(GiftRegistryApplicationConfigToken)
        public readonly config: GiftRegistryApplicationConfigInterface,
        @inject(CommonModule)
        private readonly commonModule: CommonModule,
        @inject(BaseModule)
        public readonly baseModule: BaseModule,
        @inject(DynamicImportModule)
        private readonly dynamicImportModule: DynamicImportModule,
        @inject(MutationObserverInjector)
        private readonly mutationObserverInjector: MutationObserverInjector
    ) {
        assert(config, GiftRegistryApplicationConfigSchema);

        const dynamicImportManager =
            dynamicImportModule.exports.dynamicImportManager;

        dynamicImportManager.register(AddToRegistryModalWidgetDefinition);
        dynamicImportManager.register(AddToRegistryProductPageWidgetDefinition);
        dynamicImportManager.register(GiftCartDrawerWidgetDefinition);
        dynamicImportManager.register(RegistryFormModalWidgetDefinition);
        dynamicImportManager.register(SharedRegistryPageWidgetDefinition);
        dynamicImportManager.register(GiftRegistryPageWidgetDefinition);
    }

    init(): this {
        const logger = new MinilogLogger(App.GiftRegistry);
        const locationController = this.commonModule.exports.locationController;
        const urlsBuilder = this.commonModule.exports.urlsBuilder;
        const modalManager = container.resolve(modalManagerToken);
        const customerInfoProvider = container.resolve(
            customerInfoProviderToken
        );

        const customerId = customerInfoProvider.getId();
        const localStorage = container.resolve(localStorageToken);
        const productHandle = localStorage.getItem(PRODUCT_HANDLE_STORAGE_KEY);

        if (customerId && productHandle) {
            localStorage.removeItem(PRODUCT_HANDLE_STORAGE_KEY);
            locationController.goTo(
                urlsBuilder.createProductUrl(productHandle)
            );
        }

        // Subscribes to SDK methods should be placed before SDK module registration
        subscribeToSdkMethod<OpenGiftRegistryFormModalSdkMethodPayload>(
            GIFT_REGISTRY_SDK_METHODS.OPEN_GIFT_REGISTRY_FORM_MODAL,
            ({ onSuccess, onError }) => {
                if (customerId) {
                    void this.dynamicImportModule.exports.dynamicImportManager
                        .fetch({
                            definition: RegistryFormModalWidgetDefinition,
                        })
                        .then(() => {
                            setTimeout(() => {
                                modalManager.openModal(
                                    registryFormModalToken,
                                    true,
                                    {
                                        actionType:
                                            REGISTRY_FORM_MODAL_ACTION_TYPES.create,
                                    }
                                );
                                onSuccess();
                            }, 0);
                        })
                        .catch((error: Error) => onError(error));
                } else {
                    locationController.goTo(urlsBuilder.createLoginUrl());
                }
            }
        );

        // Prepare SDK module for dynamic import
        void (async () => {
            const { sdk, sdkDynamicModulesManager, sdkMethodsCollector } =
                container.resolve(SdkModule).exports;

            const GiftRegistrySdk = await sdkDynamicModulesManager.import(
                SDK_MODULES.GIFT_REGISTRY
            );
            const giftRegistrySdk = new GiftRegistrySdk(sdkMethodsCollector);
            sdk.registerModule(SDK_MODULES.GIFT_REGISTRY, giftRegistrySdk);
        })();

        const analyticEventsCollectorModule = container.resolve(
            AnalyticsEventsCollectorModule
        );
        const eventsCollector =
            analyticEventsCollectorModule.exports.eventsCollector;
        const analyticsEventsFactory =
            analyticEventsCollectorModule.exports.eventsFactory;

        const giftCartDrawerManager = new DrawerManager();

        container.registerInstance(
            giftCartDrawerManagerToken,
            giftCartDrawerManager
        );

        container.registerInstance(giftRegistryApplicationLoggerToken, logger);
        container.register(giftRegistryAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/v1/gift-registries-service"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(globalLoggerToken)
                        ),
                        eventsCollector,
                        locationController,
                        analyticsEventsFactory
                    ),
                    di.resolve(TokenManagerModule).exports.tokenManager
                );
            }),
        });

        const addToRegistryProductPageWidgetInjector =
            new AddToRegistryProductPageWidgetInjector(
                this.config.addToRegistryProductPageWidgetPlaceholder,
                logger,
                modalManager,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const addToRegistryModalWidgetInjector =
            new AddToRegistryModalWidgetInjector(
                this.config.addToRegistryModalWidgetPlaceholder,
                logger,
                modalManager,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const sharedRegistryPageWidgetInjector =
            new SharedRegistryPageWidgetInjector(
                this.config.sharedRegistryPageWidgetPlaceholder,
                logger,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const registryFormModalWidgetInjector =
            new RegistryFormModalWidgetInjector(
                this.config.registryFormModalWidgetPlaceholder,
                logger,
                modalManager,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const giftCartDrawerWidgetInjector = new GiftCartDrawerWidgetInjector(
            this.config.giftCartDrawerWidgetPlaceholder,
            logger,
            eventsCollector,
            analyticsEventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector
        );

        addToRegistryProductPageWidgetInjector.start();
        addToRegistryModalWidgetInjector.start();
        sharedRegistryPageWidgetInjector.start();
        registryFormModalWidgetInjector.start();
        giftCartDrawerWidgetInjector.start();

        this.baseModule.exports.modulesStatusesProvider.updateModuleStatus(
            App.GiftRegistry,
            MODULE_STATUS.INITIALIZED
        );

        return this;
    }
}
